import axios from 'axios';
import { toast } from 'sonner';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000, // 30 second timeout — prevents hanging on slow queries
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - automatically attach JWT token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle 401 by clearing auth + rate-limit notifications
api.interceptors.response.use(
  (response) => {
    const warn = response.headers?.['x-rate-limit-warning'];
    if (warn) {
      toast.warning(`Heads up — you're approaching the download limit (${warn} this hour). Admin will be notified.`);
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    // Access control denial (disabled account or outside allowed IST hours) → force logout
    if (error.response?.status === 403 && error.response?.headers?.['x-access-denied']) {
      try {
        sessionStorage.setItem('accessDeniedMsg', error.response?.data?.detail || 'Your access is currently not allowed.');
      } catch { /* ignore */ }
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
      return Promise.reject(error);
    }
    if (error.response?.status === 429) {
      toast.error(error.response?.data?.detail || 'Download limit reached. Try again later or contact admin.');
    }
    // Normalise FastAPI / Pydantic validation errors (HTTP 422) so any
    // `toast.error(error.response.data.detail)` call site receives a plain
    // string instead of an array of objects. Rendering the raw array as a
    // React child triggers minified error #31 and crashes the entire UI to
    // a blank screen (see deep@yahoo.com Catalogue save report).
    const detail = error.response?.data?.detail;
    if (Array.isArray(detail)) {
      const flatten = (item) => {
        if (typeof item === 'string') return item;
        if (item?.msg) {
          const loc = Array.isArray(item.loc) ? item.loc.filter(p => p !== 'body').join('.') : '';
          return loc ? `${loc}: ${item.msg}` : item.msg;
        }
        try { return JSON.stringify(item); } catch { return String(item); }
      };
      error.response.data.detail = detail.map(flatten).join('; ');
    } else if (detail && typeof detail === 'object') {
      error.response.data.detail = detail.msg || JSON.stringify(detail);
    }
    return Promise.reject(error);
  }
);

export default api;
